import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, forwardRef, memo } from 'react';
import classnames from 'classnames';
import CSSTransition from 'react-transition-group/CSSTransition';
import { setRef } from '../utils/withForwardedRef';
import { KeyboardEventKey } from '../typings';
import useModalOpen from './useModalOpen';
import ModalManager from './ModalManager';
import Portal from './Portal';
import Backdrop from './Backdrop';
import { Root } from './Modal.styles';
export var defaultModalManager = new ModalManager();
var Modal = forwardRef(function (_a, ref) {
  var openProp = _a.open,
      id = _a.id,
      children = _a.children,
      container = _a.container,
      _b = _a.disablePortal,
      disablePortal = _b === void 0 ? false : _b,
      onRendered = _a.onRendered,
      _c = _a.BackdropComponent,
      BackdropComponent = _c === void 0 ? Backdrop : _c,
      _d = _a.disableBackdropClick,
      disableBackdropClick = _d === void 0 ? false : _d,
      _e = _a.disableCloseOnEscape,
      disableCloseOnEscape = _e === void 0 ? false : _e,
      onEscapeKeyDown = _a.onEscapeKeyDown,
      onBackdropClick = _a.onBackdropClick,
      _f = _a.hideBackdrop,
      hideBackdrop = _f === void 0 ? false : _f,
      backdropProps = _a.BackdropProps,
      onClose = _a.onClose,
      _g = _a.keepMounted,
      keepMounted = _g === void 0 ? false : _g,
      _h = _a.manager,
      manager = _h === void 0 ? defaultModalManager : _h,
      TransitionProps = _a.TransitionProps,
      ContainerComponent = _a.ContainerComponent,
      onKeyDown = _a.onKeyDown,
      className = _a.className,
      absolute = _a.absolute,
      otherProps = __rest(_a, ["open", "id", "children", "container", "disablePortal", "onRendered", "BackdropComponent", "disableBackdropClick", "disableCloseOnEscape", "onEscapeKeyDown", "onBackdropClick", "hideBackdrop", "BackdropProps", "onClose", "keepMounted", "manager", "TransitionProps", "ContainerComponent", "onKeyDown", "className", "absolute"]);

  var open = useModalOpen({
    id: id,
    open: openProp,
    onClose: onClose
  });
  var transitionDuration = TransitionProps === null || TransitionProps === void 0 ? void 0 : TransitionProps.timeout;

  var _j = useState(transitionDuration ? !open : true),
      exited = _j[0],
      setExited = _j[1];

  var modalRef = useRef(null);
  var isModalOnTop = useCallback(function () {
    return modalRef.current && (manager === null || manager === void 0 ? void 0 : manager.isTop(modalRef.current));
  }, [manager]);
  var handleKeyDown = useCallback(function (event) {
    if (event.key !== KeyboardEventKey.ESCAPE && event.key !== KeyboardEventKey.ESC || event.defaultPrevented || !isModalOnTop()) {
      return;
    }

    event.stopPropagation();

    if (onEscapeKeyDown) {
      onEscapeKeyDown(event);
    }

    if (!disableCloseOnEscape && onClose) {
      onClose();
    }
  }, [isModalOnTop, onEscapeKeyDown, disableCloseOnEscape, onClose]);
  useEffect(function () {
    if (!transitionDuration) {
      setExited(true);
    }
  }, [transitionDuration]);
  useEffect(function () {
    if (open) {
      if (transitionDuration) {
        setExited(false);
      }

      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
        manager === null || manager === void 0 ? void 0 : manager.add(modalRef.current);
      }

      return function () {
        if (modalRef.current) {
          manager === null || manager === void 0 ? void 0 : manager.remove(modalRef.current);
        }
      };
    }

    return undefined; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, manager]);
  useEffect(function () {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return function () {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }

    return undefined;
  }, [open, handleKeyDown]);
  var handleRef = useCallback(function (node) {
    if (node && !modalRef.current && open) {
      manager === null || manager === void 0 ? void 0 : manager.add(node);
    }

    modalRef.current = node;

    if (ref) {
      setRef(ref, node);
    }
  }, [manager, open, modalRef, ref]);
  var handleExited = useCallback(function () {
    var _a;

    setExited(true);
    (_a = TransitionProps === null || TransitionProps === void 0 ? void 0 : TransitionProps.onExited) === null || _a === void 0 ? void 0 : _a.call(TransitionProps);
  }, [TransitionProps]);
  var handleBackdropClick = useCallback(function (event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    onBackdropClick === null || onBackdropClick === void 0 ? void 0 : onBackdropClick(event);

    if (!disableBackdropClick) {
      onClose === null || onClose === void 0 ? void 0 : onClose();
    }
  }, [onBackdropClick, disableBackdropClick, onClose]);
  var handleRootKeyDown = useCallback(function (event) {
    handleKeyDown(event);
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
  }, [handleKeyDown, onKeyDown]);

  if (!keepMounted && !open && (!transitionDuration || exited)) {
    return null;
  }

  var content = children;

  if (transitionDuration) {
    content = _jsx(CSSTransition, __assign({
      appear: !keepMounted,
      in: open
    }, TransitionProps, {
      onExited: handleExited
    }, {
      children: content
    }));
  }

  if (ContainerComponent) {
    content = _jsx(ContainerComponent, __assign({
      onClick: BackdropComponent && !hideBackdrop ? handleBackdropClick : undefined
    }, {
      children: content
    }));
  }

  var root = _jsxs(Root, __assign({
    role: "presentation",
    id: id
  }, otherProps, {
    className: classnames(className, {
      hidden: exited,
      absolute: absolute
    }),
    ref: handleRef,
    onKeyDown: handleRootKeyDown
  }, {
    children: [BackdropComponent && !hideBackdrop && _jsx(BackdropComponent, __assign({
      open: open,
      onClick: handleBackdropClick,
      TransitionProps: {
        timeout: transitionDuration,
        appear: !keepMounted
      }
    }, backdropProps)), content]
  }));

  if (disablePortal) {
    return root;
  }

  return _jsx(Portal, __assign({
    container: container,
    onRendered: onRendered
  }, {
    children: root
  }));
});
export default memo(Modal);